import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, PackagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { logger } from "../../../lib/logger";
import { useBiz, errText } from "./i18n";
import { Card, Row, Empty, btnMain, btnSoft } from "./ui";
import { BatchEditor } from "./BatchEditor";

const Num = ({ label, value, testid }) => (
  <div className="bg-[#FAF7F2] rounded-xl px-3 py-2.5" data-testid={testid}>
    <p className="text-xs text-[#8A8173]">{label}</p>
    <p className="text-lg font-semibold" dir="ltr">{value}</p>
  </div>
);

const BatchCard = ({ b, t, aed, onEdit, onStock, onDelete }) => (
  <Card testid={`batch-${b.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-lg font-semibold">{b.perfume}</p>
        <p className="text-sm text-[#8A8173]">{b.batch_number}{b.production_date ? ` · ${b.production_date}` : ""}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={onEdit} data-testid={`batch-edit-${b.id}`} className={btnSoft}><Pencil size={14} />{t("edit")}</button>
        <button onClick={onDelete} data-testid={`batch-delete-${b.id}`} className={`${btnSoft} !text-[#A33A2B] hover:!border-[#A33A2B]`}><Trash2 size={14} />{t("delete")}</button>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 mt-4">
      <Num label={t("bottles")} value={b.bottles} testid={`batch-bottles-${b.id}`} />
      <Num label={t("sold")} value={b.sold} testid={`batch-sold-${b.id}`} />
      <Num label={t("remaining")} value={b.remaining} testid={`batch-remaining-${b.id}`} />
    </div>
    <div className="grid md:grid-cols-2 gap-x-6 mt-3">
      <div>
        <Row label={t("total_cost")} value={aed(b.total_cost)} strong testid={`batch-total-${b.id}`} />
        <Row label={t("cost_per_bottle")} value={aed(b.cost_per_bottle)} strong testid={`batch-cpb-${b.id}`} />
        <Row label={t("sell_price")} value={aed(b.sell_price)} />
        <Row label={t("profit_per_bottle")} value={aed(b.profit_per_bottle)} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#2F6B3A] mt-3 md:mt-0">{t("actual")}</p>
        <Row label={t("revenue")} value={aed(b.revenue)} testid={`batch-revenue-${b.id}`} />
        <Row label={t("profit_sold")} value={aed(b.profit_sold)} strong testid={`batch-profit-sold-${b.id}`} />
        <p className="text-[11px] uppercase tracking-wider text-[#9A7445] mt-3">{t("projection")}</p>
        <Row label={t("projected")} value={aed(b.projected_profit)} testid={`batch-projected-${b.id}`} />
      </div>
    </div>
    {b.not_in_stock > 0 && (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#FFF6E5] border border-[#F0DDB4] px-4 py-3">
        <p className="text-sm text-[#7A5A1E]">{b.not_in_stock} {t("not_in_stock")}</p>
        <button onClick={onStock} data-testid={`batch-add-stock-${b.id}`} className={btnSoft}><PackagePlus size={14} />{t("add_to_stock")}</button>
      </div>
    )}
  </Card>
);

export function Batches() {
  const { t, aed } = useBiz();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/simple/batches").then((r) => setD(r.data)).catch((e) => logger.error("batches", e)), []);
  useEffect(() => { load(); }, [load]);
  const toStock = async (b) => {
    try {
      await api.post(`/admin/simple/batches/${b.id}/add-to-stock`);
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };
  const del = async (b) => {
    if (b.sold > 0 || b.lost > 0) return toast.error(t("batch_has_sales"));
    if (!window.confirm(t("confirm_delete_batch").replace("{n}", b.batch_number).replace("{q}", b.bottles - b.not_in_stock))) return;
    try {
      await api.delete(`/admin/simple/batches/${b.id}`);
      toast.success(t("deleted"));
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };
  if (!d) return <p className="text-[#8A8173]">…</p>;
  return (
    <div data-testid="biz-batches">
      <button onClick={() => setEdit({})} disabled={!d.products.length} data-testid="batch-new" className={`${btnMain} mb-4`}><Plus size={16} />{t("new_batch")}</button>
      {d.batches.length === 0 ? <Empty>{t("no_batches")}</Empty> : (
        <div className="space-y-4">{d.batches.map((b) => <BatchCard key={b.id} b={b} t={t} aed={aed} onEdit={() => setEdit({ batch: b })} onStock={() => toStock(b)} onDelete={() => del(b)} />)}</div>
      )}
      {edit && <BatchEditor batch={edit.batch} products={d.products} onClose={() => { setEdit(null); load(); }} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
